import { ReactNode } from "react";
import { Control, FieldPath } from "react-hook-form";
import { Trash2Icon } from "lucide-react";
import { ImageUpload } from "@/view/components/ImageUpload";
import { FormField, FormItem, FormMessage } from "@/view/components/ui/form";
import { withCdn } from "@/app/utils/withCdn";
import type { FormSchema } from "./useConfigPartner";

type ProductImageFieldName = Extract<FieldPath<FormSchema>, "image" | "imageBlack">;

interface ProductImageFieldProps {
  control: Control<FormSchema>;
  name: ProductImageFieldName;
  placeholderImage?: string;
  alt: string;
  canEdit: boolean;
  className: string;
  style?: React.CSSProperties;
  onRemove: (e: React.MouseEvent<HTMLButtonElement>) => void;
  overlay?: ReactNode;
}

export const ProductImageField = ({
  control,
  name,
  placeholderImage,
  alt,
  canEdit,
  className,
  style,
  onRemove,
  overlay,
}: ProductImageFieldProps) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className="relative">
        {overlay}
        <ImageUpload
          className={className}
          style={style}
          onChange={(files) => field.onChange(files.at(-1))}
          value={field.value ? [field.value as File | string] : null}
          dropzoneOptions={{ disabled: !canEdit }}
          render={({ value }) => {
            const image = value?.[0] as File | string;
            const defaultImage = typeof image === "string";

            if (!image) {
              if (!canEdit && !placeholderImage) {
                return <div className="h-full w-full" />;
              }
              return (
                <ImageUpload.Placeholder
                  className="h-full min-h-0 justify-center"
                  image={placeholderImage}
                />
              );
            }

            return (
              <div className="group">
                <img
                  src={
                    defaultImage
                      ? withCdn(image)
                      : (image && URL.createObjectURL(image as File)) || ""
                  }
                  alt={alt}
                  className="h-full w-full object-contain p-16"
                />
                {canEdit && (
                  <button
                    type="button"
                    className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-white/90 text-xs p-2 font-medium text-red-500 shadow-sm backdrop-blur-sm opacity-0 transition-all duration-200 hover:bg-red-50 hover:shadow-md group-hover:opacity-100"
                    onClick={onRemove}
                  >
                    <Trash2Icon size={16} className="text-red-500" />
                  </button>
                )}
              </div>
            );
          }}
        />
        <FormMessage className="text-xs" />
      </FormItem>
    )}
  />
);
